import { useRef, useState } from 'react';
import {
  HomeWidgetScrollBottomFade,
  homeWidgetScrollListClassName,
  useHomeWidgetScrollHint,
} from '../WidgetScrollList';
import { HeaderTabs } from '@/components/ui/HeaderTabs';
import type { HomeDeadline } from '@/hooks/useHomeDashboard';
import { DeadlineRow } from '../DeadlineRow';
import {
  DeadlineWidgetStatus,
  firstNonEmptyWeekOffset,
  type WeekSection,
  weekTabLabel,
} from './shared';

export function DeadlineWeekTabs({
  weeks,
  openDeadline,
  isLoading,
  isError,
  onRetry,
  idBase,
  listClassName,
  emptyClassName = 'py-6 text-center text-sm text-slate-400',
  className,
}: {
  weeks: WeekSection[];
  openDeadline: (deadline: HomeDeadline) => void;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  idBase: string;
  listClassName: string;
  emptyClassName?: string;
  className?: string;
}) {
  const [tab, setTab] = useState<string | null>(null);
  const activeTab = tab ?? String(firstNonEmptyWeekOffset(weeks));
  const active = weeks.find((week) => String(week.offset) === activeTab) ?? weeks[0];
  const listRef = useRef<HTMLUListElement>(null);
  // Loading and error unmount the list, so a retry must rebind when the tab
  // and row count stay the same.
  const showScrollFade = useHomeWidgetScrollHint(listRef, [
    activeTab,
    active?.deadlines.length ?? 0,
    isLoading,
    isError,
  ]);

  return (
    <DeadlineWidgetStatus isLoading={isLoading} isError={isError} onRetry={onRetry}>
      <div className={className}>
        <div className="border-b border-slate-200">
          <HeaderTabs
            idBase={idBase}
            ariaLabel="Deadline weeks"
            value={activeTab}
            onChange={(value) => setTab(value)}
            className="gap-0"
            tabClassName="text-xs text-slate-400 hover:text-slate-500"
            activeTabClassName="text-slate-500"
            items={weeks.map((week) => ({
              value: String(week.offset),
              label: weekTabLabel(week.offset, week.start),
            }))}
          />
        </div>
        <div
          role="tabpanel"
          id={`${idBase}-panel-${activeTab}`}
          aria-labelledby={`${idBase}-tab-${activeTab}`}
        >
          {active && active.deadlines.length === 0 ? (
            <p className={emptyClassName}>No deadlines.</p>
          ) : (
            <div className="relative min-h-0">
              <ul ref={listRef} className={listClassName}>
                {active?.deadlines.map((deadline) => (
                  <DeadlineRow
                    key={deadline.task_guid}
                    deadline={deadline}
                    onOpen={openDeadline}
                  />
                ))}
              </ul>
              <HomeWidgetScrollBottomFade visible={showScrollFade} />
            </div>
          )}
        </div>
      </div>
    </DeadlineWidgetStatus>
  );
}
